import React from 'react';
import { useRecoilValue } from 'recoil';
import { preorderState, shopifyState } from '../../recoil/atoms';
import { SelectionType } from '../../enums/SelectionType.enum';
import { VariantStatus } from '../../enums/VariantStatus.enum';
import { useProductSelection } from '../../hooks/useProductSelection';
import { useProductRating } from '../../hooks/useProductRating';
import { useProductPrice } from '../../hooks/useProductPrice';
import { useSharedGetReviewState } from '../../hooks/actions/useReviewState';
import { buildParams, getPreorder, variantStatus } from '../../util';
import { Product } from 'schema-dts';
import { JsonLd } from 'react-schemaorg';

export const ProductDetailSchema: React.FC = () => {
  const { product, color, size, variant } = useProductSelection(SelectionType.ProductSection, '0');
  const { reviewSet } = useSharedGetReviewState({ productId: product.internalId, page: 1, itemsPerPage: 10 });
  const preorders = useRecoilValue(preorderState);
  const rating = useProductRating(product);
  const shopify = useRecoilValue(shopifyState);
  const currency = shopify?.currency.active;
  const price = useProductPrice(product, variant, color);
  const paramString = buildParams({ color, size });
  const preorder = variant && getPreorder(preorders, variant);
  const status = variant && variantStatus(preorder, variant);
  const getAvailability = () => {
    switch (status) {
      case undefined:
      case VariantStatus.Available:
        return 'https://schema.org/InStock';
      case VariantStatus.PreOrder:
        return 'https://schema.org/PreOrder';
      case VariantStatus.SoldOut:
        return 'https://schema.org/SoldOut';
      default:
        return 'https://schema.org/OutOfStock';
    }
  };
  const url = `${window.location.protocol}//${window.location.host}${window.location.pathname}${color && size ? paramString : ''}`;
  const now = new Date();
  return (
    <JsonLd<Product>
      item={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        aggregateRating:
          rating.totalReviews > 0
            ? {
                '@type': 'AggregateRating',
                ratingValue: rating.rating,
                reviewCount: rating.totalReviews,
                bestRating: 5,
                worstRating: 1,
              }
            : undefined,
        brand: {
          '@type': 'Brand',
          name: 'Honeylove',
          logo: 'https://cdn.shopify.com/s/files/1/0965/6676/files/honeylove_black_logo.png',
          url: 'https://www.honeylove.com',
        },
        name: variant?.name ?? product.title,
        description: product.description,
        sku: variant?.sku ?? product.sku,
        gtin: variant?.barcode ?? undefined,
        offers: {
          '@type': 'Offer',
          availability: getAvailability(),
          price: price.replace(/[^0-9\.]+/, ''),
          priceValidUntil: new Date(now.setMonth(now.getMonth() + 3)).toUTCString(), // 3 months from today
          priceCurrency: currency,
          url: url,
        },
        review: reviewSet.reviews?.map((v) => {
          return {
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: v.user.display_name,
            },
            name: v.title,
            datePublished: v.created_at,
            reviewBody: v.content,
            reviewRating: {
              '@type': 'Rating',
              bestRating: 5,
              worstRating: 1,
              ratingValue: v.score,
            },
          };
        }),
        image: variant?.featured_image.src ?? product.featuredImage,
        url: url,
      }}
    />
  );
};

export default ProductDetailSchema;
